import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';

// ─────────────────────────────────────────────────────────────────
// HOW TO SET UP WEB3FORMS (FREE — 250 submissions/month):
//
//  1. Go to https://web3forms.com
//  2. Enter your email address → click "Create Access Key"
//  3. You'll receive an email with your Access Key
//  4. Replace YOUR_WEB3FORMS_ACCESS_KEY below with that key
//  5. Every form submission will be emailed to you instantly
// ─────────────────────────────────────────────────────────────────
const WEB3FORMS_KEY = '900b5fca-4b67-439d-a812-5d10f3a1cdda';

const ROLES = [
  'Licensed Solar Installer',
  'EPC Contractor',
  'General Contractor',
  'Roofing Company',
  'Project Investor',
  'Solar Lender / Financing Provider',
  'Tax Equity Investor',
  'Sales Partner / Referral Agent',
  'Real Estate Agent',
  'Utility / Energy Consultant',
  'Media / Press',
  'Nonprofit / Community Organization',
  'Other',
];

const inputStyle = {
  width: '100%', padding: '0.875rem 1rem', borderRadius: 10,
  border: '1.5px solid var(--gray-200)', fontFamily: 'DM Sans, sans-serif',
  fontSize: '0.95rem', color: 'var(--navy)', background: 'var(--gray-50)',
  outline: 'none', transition: 'border-color 0.2s',
};
const labelStyle = {
  display: 'block', fontSize: '0.875rem', fontWeight: 600,
  color: 'var(--navy)', marginBottom: '0.4rem',
};

export default function Contact() {
  const [form, setForm] = useState({
    role: '', company: '', contact: '', email: '', phone: '', state: 'California', message: '',
  });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.role || !form.company || !form.contact || !form.email || !form.message) {
      setError('Please fill in all required fields.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const payload = {
        access_key: WEB3FORMS_KEY,
        subject: `[SolarSavings] New Partnership Inquiry — ${form.company}`,
        from_name: form.contact,
        ...form,
      };
      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (data.success) setSent(true);
      else setError('Something went wrong. Please try again.');
    } catch {
      setError('Network error. Please try again.');
    }
    setLoading(false);
  };

  return (
    <>
      <Helmet>
        <title>Contact SolarSavings – Free Solar Consultation in California</title>
        <meta name="description" content="Talk to an independent California solar advisor — no sales pitch, no installer bias. Get honest answers on NEM 3.0, SGIP rebates, and your 25-year ROI. Free consultation." />
        <link rel="canonical" href="https://solarsavings.com/contact" />
      </Helmet>
      {/* HERO */}
      <div style={{
        background: 'var(--navy)', minHeight: '30vh', display: 'flex', alignItems: 'center',
        padding: '7rem 2.5rem 4rem', position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'repeating-linear-gradient(45deg, rgba(245,166,35,0.04) 0px, rgba(245,166,35,0.04) 1px, transparent 1px, transparent 40px)' }} />
        <div style={{ position: 'absolute', right: -80, bottom: -80, width: 420, height: 420, borderRadius: '50%', background: 'radial-gradient(circle, rgba(245,166,35,0.1) 0%, transparent 70%)' }} />
        <div style={{ maxWidth: 700, margin: '0 auto', width: '100%', textAlign: 'center', position: 'relative', zIndex: 1 }}>
          <span style={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--gold)', display: 'block', marginBottom: '0.8rem' }}>Contact</span>
          <h1 style={{ fontFamily: 'Sora, sans-serif', fontSize: 'clamp(1.8rem, 3.5vw, 2.75rem)', fontWeight: 800, color: '#fff', lineHeight: 1.15, marginBottom: '1rem' }}>
            Let's Work Together
          </h1>
          <p style={{ fontSize: '1.05rem', color: 'rgba(255,255,255,0.68)', lineHeight: 1.7, maxWidth: 520, margin: '0 auto' }}>
            Tell us about yourself and your project. We respond to all inquiries within 1 business day.
          </p>
        </div>
      </div>

      {/* FORM */}
      <section style={{ background: 'var(--gray-50)', padding: '4rem 2.5rem 6rem' }}>
        <motion.div
          initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
          style={{ maxWidth: 720, margin: '0 auto', background: '#fff', borderRadius: 20, boxShadow: '0 20px 60px rgba(26,43,95,0.08)', padding: 'clamp(2rem, 5vw, 3rem)', border: '1px solid var(--gray-100)' }}>

          {sent ? (
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4 }}
              style={{ textAlign: 'center', padding: '3rem 2rem' }}>
              <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#d1fae5', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem', fontSize: '1.75rem' }}>✅</div>
              <h2 style={{ fontFamily: 'Sora, sans-serif', fontWeight: 800, fontSize: '1.5rem', color: 'var(--navy)', marginBottom: '0.75rem' }}>Message Received!</h2>
              <p style={{ color: 'var(--gray-600)', lineHeight: 1.7, maxWidth: 380, margin: '0 auto' }}>
                Thank you, <strong>{form.contact}</strong>. Our team will review your inquiry and get back to you at <strong>{form.email}</strong> within 1 business day.
              </p>
            </motion.div>
          ) : (
            <form onSubmit={handleSubmit} noValidate>

              {/* Role */}
              <div style={{ marginBottom: '1.4rem' }}>
                <label style={labelStyle}>I am a... *</label>
                <select value={form.role} onChange={e => set('role', e.target.value)} required style={inputStyle}>
                  <option value="">Select your role</option>
                  {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>

              {/* Company + Name */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.4rem' }}>
                <div>
                  <label style={labelStyle}>Company Name *</label>
                  <input type="text" required placeholder="Your company" value={form.company} onChange={e => set('company', e.target.value)} style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Your Name *</label>
                  <input type="text" required placeholder="Jane Smith" value={form.contact} onChange={e => set('contact', e.target.value)} style={inputStyle} />
                </div>
              </div>

              {/* Email + Phone */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.4rem' }}>
                <div>
                  <label style={labelStyle}>Email *</label>
                  <input type="email" required placeholder="you@company.com" value={form.email} onChange={e => set('email', e.target.value)} style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Phone</label>
                  <input type="tel" placeholder="(555) 000-0000" value={form.phone} onChange={e => set('phone', e.target.value)} style={inputStyle} />
                </div>
              </div>

              {/* State */}
              <div style={{ marginBottom: '1.4rem' }}>
                <label style={labelStyle}>State / Region</label>
                <select value={form.state} onChange={e => set('state', e.target.value)} style={inputStyle}>
                  <option>California</option>
                  <option>Other US State</option>
                  <option>Outside US</option>
                </select>
              </div>

              {/* Message */}
              <div style={{ marginBottom: '1.4rem' }}>
                <label style={labelStyle}>Tell us about your project or partnership idea *</label>
                <textarea
                  required
                  placeholder="e.g. We are a licensed installer based in Fresno looking to refer commercial clients and collaborate on SGIP projects..."
                  value={form.message}
                  onChange={e => set('message', e.target.value)}
                  style={{ ...inputStyle, minHeight: 130, resize: 'vertical' }}
                />
              </div>

              {/* Error */}
              {error && (
                <div style={{ background: '#fee2e2', color: '#991b1b', borderRadius: 10, padding: '0.75rem 1rem', fontSize: '0.875rem', marginBottom: '1.25rem' }}>
                  {error}
                </div>
              )}

              <button type="submit" disabled={loading}
                style={{ width: '100%', padding: '1rem', background: loading ? 'var(--gray-200)' : 'var(--gold)', color: loading ? 'var(--gray-400)' : 'var(--navy)', border: 'none', borderRadius: 50, fontFamily: 'Sora, sans-serif', fontWeight: 700, fontSize: '1rem', cursor: loading ? 'not-allowed' : 'pointer', transition: 'all 0.3s' }}>
                {loading ? 'Sending...' : 'Send My Inquiry →'}
              </button>
              <p style={{ textAlign: 'center', fontSize: '0.8rem', color: 'var(--gray-400)', marginTop: '0.75rem' }}>
                🔒 Your information is never sold or shared.
              </p>
            </form>
          )}
        </motion.div>
      </section>
    </>
  );
}
